// ============================================================
// BlueDart Shipping Calculator v3
// Chronos Lights — Shopify Theme Asset
//
// RATE STRUCTURE (Dartplus Surface):
// Zone         | 1st 500g | Additional 500g
// Intracity    |    28    |      26
// Intra-region |    29    |      27
// Metro        |    30    |      28
// ROI          |    35    |      33
// NE & JK      |    45    |      43
//
// FORMULA:
// base = first_500g + (additional_slabs * addl_rate)
// subtotal = base * (1 + FS% + CAF%)
// final = ceil(subtotal * 1.18)  <- 18% GST
//
// MONTHLY UPDATE: Check FS and CAF on BlueDart website
// FS:  https://www.bluedart.com/web/guest/fuel-surcharge
// CAF: https://www.bluedart.com/web/guest/currency-adjustment-factor
// ============================================================

(function () {

  // ============================================================
  // CONFIGURATION
  // ============================================================
  const SHIPPING_VARIANT_ID    = '48031684690168';
  const PACKAGING_BUFFER_GRAMS = 0; // Weight entered in Shopify already includes box/volumetric

  // BlueDart Dartplus Surface rates
  const RATES = {
    intracity:   { first: 28, addl: 26 },
    intraregion: { first: 29, addl: 27 },
    metro:       { first: 30, addl: 28 },
    roi:         { first: 35, addl: 33 },
    nejk:        { first: 45, addl: 43 },
  };

  // Surcharge rates — update monthly from BlueDart mechanism pages
  const FS_RATE  = 0.285;    // Fuel Surcharge — check monthly
  const CAF_RATE = 0.11565;  // Currency Adj. Factor — check monthly
  const GST_RATE = 0.18;     // 18% GST — fixed

  // COD charges
  const COD_MIN = 30;
  const COD_PCT = 0.015; // 1.5%

  // ============================================================
  // RATE CALCULATION
  // ============================================================

  function calcBase(weightG, zone) {
    const r = RATES[zone];
    if (weightG <= 500) return r.first;
    const slabs = Math.ceil((weightG - 500) / 500);
    return r.first + (slabs * r.addl);
  }

  function calcShipping(weightG, zone) {
    const base     = calcBase(weightG, zone);
    const subtotal = base * (1 + FS_RATE + CAF_RATE);
    return Math.ceil(subtotal * (1 + GST_RATE));
  }

  function getDeliveryDate(days) {
    const d = new Date();
    d.setDate(d.getDate() + days + 1);
    return d.toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short'
    });
  }

  function getZoneLabel(zone) {
    const labels = {
      intracity:   'Same City',
      intraregion: 'Nearby Region',
      metro:       'Metro',
      roi:         'Rest of India',
      nejk:        'NE & J&K',
    };
    return labels[zone] || zone;
  }

  // ============================================================
  // CART OPERATIONS
  // ============================================================

  async function getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function removeShippingItem() {
    const cart = await getCart();
    const existing = cart.items.find(
      i => String(i.variant_id) === String(SHIPPING_VARIANT_ID)
    );
    if (existing) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: existing.key, quantity: 0 })
      });
    }
  }

  async function setShippingItem(chargeRs, pincode, zone, cod) {
    await removeShippingItem();

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          _shipping_charge:  String(chargeRs),
          _shipping_pincode: String(pincode),
          _shipping_zone:    String(zone),
          _cod_available:    cod ? 'yes' : 'no',
        }
      })
    });

    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: SHIPPING_VARIANT_ID,
        quantity: 1,
        properties: {
          _shipping_display: 'yes',
          _shipping_amount:  chargeRs,
        }
      })
    });
  }

  // ============================================================
  // UI UPDATE
  // ============================================================

  function updateTotalsDisplay(shippingCharge, productSubtotal) {
    const shippingEl = document.getElementById('bd-shipping-display');
    if (shippingEl) {
      shippingEl.textContent = `₹${shippingCharge.toLocaleString('en-IN')}`;
      shippingEl.style.fontWeight = '600';
      shippingEl.style.color = '#1a1a1a';
    }

    const totalEl = document.getElementById('bd-grand-total');
    if (totalEl) {
      totalEl.textContent = `₹${(productSubtotal + shippingCharge).toLocaleString('en-IN')}`;
    }

    const nativeSubtotal = document.querySelector('.subtotal .theme-money');
    if (nativeSubtotal) {
      nativeSubtotal.textContent = `₹${(productSubtotal + shippingCharge).toLocaleString('en-IN')}`;
    }
  }

  // ============================================================
  // MAIN CALCULATE FUNCTION
  // ============================================================

  window.calculateBlueDartShipping = async function () {
    const input   = document.getElementById('bd-pincode-input');
    const btn     = document.getElementById('bd-check-btn');
    const pincode = input ? input.value.trim() : '';

    if (!/^\d{6}$/.test(pincode)) {
      showResult('error', '⚠️ Please enter a valid 6-digit PIN code.');
      return;
    }

    if (btn) { btn.textContent = 'Checking...'; btn.disabled = true; }

    try {
      if (!window.BLUEDART_PINS) {
        showResult('error', '⚠️ PIN database not loaded. Please refresh the page.');
        return;
      }

      const pinInfo = window.BLUEDART_PINS[pincode];

      if (!pinInfo) {
        showResult('error', '❌ Delivery not available at this PIN code.');
        await removeShippingItem();
        return;
      }

      // Get cart weight
      const cart = await getCart();
      let totalWeight     = PACKAGING_BUFFER_GRAMS;
      let productSubtotal = 0;

      cart.items.forEach(item => {
        if (String(item.variant_id) === String(SHIPPING_VARIANT_ID)) return;
        totalWeight     += (item.grams || 0) * item.quantity;
        productSubtotal += item.final_line_price / 100;
      });

      const zone           = pinInfo.z;
      const codAvailable   = pinInfo.c;
      const deliveryDays   = pinInfo.d;
      const shippingCharge = calcShipping(totalWeight, zone);
      const deliveryDate   = getDeliveryDate(deliveryDays);
      const zoneLabel      = getZoneLabel(zone);

      await setShippingItem(shippingCharge, pincode, zone, codAvailable);
      updateTotalsDisplay(shippingCharge, productSubtotal);

      try {
        localStorage.setItem('bd_last_pin', pincode);
        localStorage.setItem('bd_shipping_charge', shippingCharge);
      } catch(e) {}

      showResult('success', `
        <div class="bd-result-box">
          <div class="bd-result-row">
            <span class="bd-label">📦 BlueDart Delivery</span>
            <span class="bd-charge">₹${shippingCharge.toLocaleString('en-IN')}</span>
          </div>
          <div class="bd-result-row bd-muted-row">
            <span>🗓️ Estimated delivery by <strong>${deliveryDate}</strong> (${deliveryDays} day${deliveryDays > 1 ? 's' : ''})</span>
          </div>
          <div class="bd-result-row bd-muted-row">
            <span>📍 ${zoneLabel}</span>
          </div>
          <div class="bd-result-row">
            <span class="${codAvailable ? 'bd-cod-yes' : 'bd-cod-no'}">
              ${codAvailable ? '✅ Cash on Delivery available' : '❌ COD not available at this PIN'}
            </span>
          </div>
        </div>
      `);

    } catch (err) {
      console.error('Shipping error:', err);
      showResult('error', '⚠️ Unable to calculate shipping. Please try again.');
    } finally {
      if (btn) { btn.textContent = 'Check'; btn.disabled = false; }
    }
  };

  // ============================================================
  // UI HELPERS
  // ============================================================

  function showResult(type, html) {
    const div = document.getElementById('bd-shipping-result');
    if (!div) return;
    div.style.display = 'block';
    div.className     = `bd-result bd-result--${type}`;
    div.innerHTML     = html;
  }

  // ============================================================
  // INIT
  // ============================================================

  document.addEventListener('DOMContentLoaded', function () {
    const input = document.getElementById('bd-pincode-input');
    if (input) {
      input.addEventListener('input', function () {
        this.value = this.value.replace(/\D/g, '').slice(0, 6);
      });
      input.addEventListener('keypress', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          window.calculateBlueDartShipping();
        }
      });
    }

    try {
      const saved = localStorage.getItem('bd_last_pin');
      if (saved && input) {
        input.value = saved;
        setTimeout(() => window.calculateBlueDartShipping(), 500);
      }
    } catch(e) {}
  });

})();